import { useState } from 'react';
import { Palette, Save, RotateCcw, Plus, Trash2, Copy, Check } from 'lucide-react';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { DensityToggle } from '@/components/DensityToggle';
import { ThemeButton } from '@/components/ui/theme/ThemeButton';
import { ThemeCard, ThemeCardHeader, ThemeCardTitle, ThemeCardContent } from '@/components/ui/theme/ThemeCard';
import { ColorPicker } from '@/components/ui/ColorPicker';

interface ColorToken {
    name: string;
    value: string;
    description: string;
    category: 'core' | 'layout' | 'components';
    isGradient: boolean;
    gradientStops?: { color: string; position: number }[];
    usage: string[];
}

interface PaletteData {
    id: string;
    name: string;
    description: string;
    colors: ColorToken[];
}

export function PaletteEditorPage() {
    const [selectedPalette, setSelectedPalette] = useState('slate');
    const [editingToken, setEditingToken] = useState<ColorToken | null>(null);
    const [isGradient, setIsGradient] = useState(false);
    const [gradientStops, setGradientStops] = useState<{ color: string; position: number }[]>([
        { color: '#3b82f6', position: 0 },
        { color: '#1d4ed8', position: 100 }
    ]);
    const [copiedValue, setCopiedValue] = useState<string | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Define all palettes with their color variables
    const [palettes, setPalettes] = useState<PaletteData[]>([
        {
            id: 'slate',
            name: 'Slate Professional',
            description: 'Cool slate tones for enterprise applications',
            colors: [
                // Core colors
                { name: 'background', value: '210 20% 98%', description: 'Main background color', category: 'core', isGradient: false, usage: ['Main page background', 'Card backgrounds'] },
                { name: 'foreground', value: '224 71% 4%', description: 'Main text color', category: 'core', isGradient: false, usage: ['Primary text', 'Headings'] },
                { name: 'primary', value: '221 83% 53%', description: 'Primary brand color', category: 'core', isGradient: false, usage: ['Primary buttons', 'Links', 'Accents'] },
                { name: 'secondary', value: '215 16% 47%', description: 'Secondary brand color', category: 'core', isGradient: false, usage: ['Secondary buttons', 'Secondary text'] },
                { name: 'accent', value: '222 47% 11%', description: 'Accent color for highlights', category: 'core', isGradient: false, usage: ['Hover states', 'Focus rings'] },
                { name: 'muted', value: '220 14% 96%', description: 'Muted background color', category: 'core', isGradient: false, usage: ['Muted backgrounds', 'Disabled states'] },
                { name: 'border', value: '214 32% 91%', description: 'Border color', category: 'core', isGradient: false, usage: ['Borders', 'Dividers'] },
                { name: 'ring', value: '221 83% 53%', description: 'Focus ring color', category: 'core', isGradient: false, usage: ['Focus indicators'] },

                // Layout colors
                {
                    name: 'topbar-bg', value: '221 83% 53%', description: 'Top navigation background', category: 'layout', isGradient: true, usage: ['Header background'], gradientStops: [
                        { color: 'hsl(221 83% 53%)', position: 0 },
                        { color: 'hsl(221 83% 48%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-bg', value: '220 14% 97%', description: 'Sidebar background', category: 'layout', isGradient: true, usage: ['Sidebar background'], gradientStops: [
                        { color: 'hsl(220 14% 97%)', position: 0 },
                        { color: 'hsl(220 14% 95%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-active', value: '221 83% 53%', description: 'Active sidebar item', category: 'layout', isGradient: true, usage: ['Active menu items'], gradientStops: [
                        { color: 'hsl(221 83% 53%)', position: 0 },
                        { color: 'hsl(221 83% 48%)', position: 100 }
                    ]
                },
                { name: 'main-bg', value: '210 20% 98%', description: 'Main content background', category: 'layout', isGradient: false, usage: ['Main content area'] },
                { name: 'panel-bg', value: '0 0% 100%', description: 'Panel background', category: 'layout', isGradient: false, usage: ['Panel backgrounds'] },
                { name: 'card-bg', value: '0 0% 100%', description: 'Card background', category: 'layout', isGradient: false, usage: ['Card backgrounds'] },

                // Component colors
                {
                    name: 'button-bg-primary', value: '221 83% 53%', description: 'Primary button background', category: 'components', isGradient: true, usage: ['Primary buttons'], gradientStops: [
                        { color: 'hsl(221 83% 53%)', position: 0 },
                        { color: 'hsl(221 83% 48%)', position: 100 }
                    ]
                },
                {
                    name: 'button-bg-secondary', value: '215 16% 47%', description: 'Secondary button background', category: 'components', isGradient: true, usage: ['Secondary buttons'], gradientStops: [
                        { color: 'hsl(215 16% 47%)', position: 0 },
                        { color: 'hsl(215 16% 42%)', position: 100 }
                    ]
                },
                { name: 'button-bg-ghost', value: 'transparent', description: 'Ghost button background', category: 'components', isGradient: false, usage: ['Ghost buttons'] },
                {
                    name: 'badge-bg-neutral', value: '220 20% 95%', description: 'Neutral badge background', category: 'components', isGradient: true, usage: ['Neutral badges'], gradientStops: [
                        { color: 'hsl(220 20% 95%)', position: 0 },
                        { color: 'hsl(220 15% 90%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-info', value: '200 100% 95%', description: 'Info badge background', category: 'components', isGradient: true, usage: ['Info badges'], gradientStops: [
                        { color: 'hsl(200 100% 95%)', position: 0 },
                        { color: 'hsl(200 80% 88%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-success', value: '140 60% 92%', description: 'Success badge background', category: 'components', isGradient: true, usage: ['Success badges'], gradientStops: [
                        { color: 'hsl(140 60% 92%)', position: 0 },
                        { color: 'hsl(140 50% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-warning', value: '45 90% 92%', description: 'Warning badge background', category: 'components', isGradient: true, usage: ['Warning badges'], gradientStops: [
                        { color: 'hsl(45 90% 92%)', position: 0 },
                        { color: 'hsl(45 80% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-danger', value: '0 70% 92%', description: 'Danger badge background', category: 'components', isGradient: true, usage: ['Danger badges'], gradientStops: [
                        { color: 'hsl(0 70% 92%)', position: 0 },
                        { color: 'hsl(0 60% 85%)', position: 100 }
                    ]
                },
            ]
        },
        {
            id: 'emerald',
            name: 'Emerald Gradient',
            description: 'Vibrant green palette with nature-inspired tones',
            colors: [
                // Core colors
                { name: 'background', value: '210 20% 98%', description: 'Main background color', category: 'core', isGradient: false, usage: ['Main page background', 'Card backgrounds'] },
                { name: 'foreground', value: '224 71% 4%', description: 'Main text color', category: 'core', isGradient: false, usage: ['Primary text', 'Headings'] },
                { name: 'primary', value: '142 76% 36%', description: 'Primary brand color', category: 'core', isGradient: false, usage: ['Primary buttons', 'Links', 'Accents'] },
                { name: 'secondary', value: '215 16% 47%', description: 'Secondary brand color', category: 'core', isGradient: false, usage: ['Secondary buttons', 'Secondary text'] },
                { name: 'accent', value: '142 76% 36%', description: 'Accent color for highlights', category: 'core', isGradient: false, usage: ['Hover states', 'Focus rings'] },
                { name: 'muted', value: '220 14% 96%', description: 'Muted background color', category: 'core', isGradient: false, usage: ['Muted backgrounds', 'Disabled states'] },
                { name: 'border', value: '214 32% 91%', description: 'Border color', category: 'core', isGradient: false, usage: ['Borders', 'Dividers'] },
                { name: 'ring', value: '142 76% 36%', description: 'Focus ring color', category: 'core', isGradient: false, usage: ['Focus indicators'] },

                // Layout colors
                {
                    name: 'topbar-bg', value: '142 76% 36%', description: 'Top navigation background', category: 'layout', isGradient: true, usage: ['Header background'], gradientStops: [
                        { color: 'hsl(142 76% 36%)', position: 0 },
                        { color: 'hsl(142 76% 31%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-bg', value: '220 14% 97%', description: 'Sidebar background', category: 'layout', isGradient: true, usage: ['Sidebar background'], gradientStops: [
                        { color: 'hsl(220 14% 97%)', position: 0 },
                        { color: 'hsl(220 14% 95%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-active', value: '142 76% 36%', description: 'Active sidebar item', category: 'layout', isGradient: true, usage: ['Active menu items'], gradientStops: [
                        { color: 'hsl(142 76% 36%)', position: 0 },
                        { color: 'hsl(142 76% 31%)', position: 100 }
                    ]
                },
                { name: 'main-bg', value: '210 20% 98%', description: 'Main content background', category: 'layout', isGradient: false, usage: ['Main content area'] },
                { name: 'panel-bg', value: '0 0% 100%', description: 'Panel background', category: 'layout', isGradient: false, usage: ['Panel backgrounds'] },
                { name: 'card-bg', value: '0 0% 100%', description: 'Card background', category: 'layout', isGradient: false, usage: ['Card backgrounds'] },

                // Component colors
                {
                    name: 'button-bg-primary', value: '142 76% 36%', description: 'Primary button background', category: 'components', isGradient: true, usage: ['Primary buttons'], gradientStops: [
                        { color: 'hsl(142 76% 36%)', position: 0 },
                        { color: 'hsl(142 76% 31%)', position: 100 }
                    ]
                },
                {
                    name: 'button-bg-secondary', value: '215 16% 47%', description: 'Secondary button background', category: 'components', isGradient: true, usage: ['Secondary buttons'], gradientStops: [
                        { color: 'hsl(215 16% 47%)', position: 0 },
                        { color: 'hsl(215 16% 42%)', position: 100 }
                    ]
                },
                { name: 'button-bg-ghost', value: 'transparent', description: 'Ghost button background', category: 'components', isGradient: false, usage: ['Ghost buttons'] },
                {
                    name: 'badge-bg-neutral', value: '220 20% 95%', description: 'Neutral badge background', category: 'components', isGradient: true, usage: ['Neutral badges'], gradientStops: [
                        { color: 'hsl(220 20% 95%)', position: 0 },
                        { color: 'hsl(220 15% 90%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-info', value: '200 100% 95%', description: 'Info badge background', category: 'components', isGradient: true, usage: ['Info badges'], gradientStops: [
                        { color: 'hsl(200 100% 95%)', position: 0 },
                        { color: 'hsl(200 80% 88%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-success', value: '140 60% 92%', description: 'Success badge background', category: 'components', isGradient: true, usage: ['Success badges'], gradientStops: [
                        { color: 'hsl(140 60% 92%)', position: 0 },
                        { color: 'hsl(140 50% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-warning', value: '45 90% 92%', description: 'Warning badge background', category: 'components', isGradient: true, usage: ['Warning badges'], gradientStops: [
                        { color: 'hsl(45 90% 92%)', position: 0 },
                        { color: 'hsl(45 80% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-danger', value: '0 70% 92%', description: 'Danger badge background', category: 'components', isGradient: true, usage: ['Danger badges'], gradientStops: [
                        { color: 'hsl(0 70% 92%)', position: 0 },
                        { color: 'hsl(0 60% 85%)', position: 100 }
                    ]
                },
            ]
        },
        {
            id: 'violet',
            name: 'Violet Modern',
            description: 'Modern purple theme',
            colors: [
                // Core colors
                { name: 'background', value: '210 20% 98%', description: 'Main background color', category: 'core', isGradient: false, usage: ['Main page background', 'Card backgrounds'] },
                { name: 'foreground', value: '224 71% 4%', description: 'Main text color', category: 'core', isGradient: false, usage: ['Primary text', 'Headings'] },
                { name: 'primary', value: '238 100% 67%', description: 'Primary brand color', category: 'core', isGradient: false, usage: ['Primary buttons', 'Links', 'Accents'] },
                { name: 'secondary', value: '215 16% 47%', description: 'Secondary brand color', category: 'core', isGradient: false, usage: ['Secondary buttons', 'Secondary text'] },
                { name: 'accent', value: '238 100% 67%', description: 'Accent color for highlights', category: 'core', isGradient: false, usage: ['Hover states', 'Focus rings'] },
                { name: 'muted', value: '220 14% 96%', description: 'Muted background color', category: 'core', isGradient: false, usage: ['Muted backgrounds', 'Disabled states'] },
                { name: 'border', value: '214 32% 91%', description: 'Border color', category: 'core', isGradient: false, usage: ['Borders', 'Dividers'] },
                { name: 'ring', value: '238 100% 67%', description: 'Focus ring color', category: 'core', isGradient: false, usage: ['Focus indicators'] },

                // Layout colors
                {
                    name: 'topbar-bg', value: '238 100% 67%', description: 'Top navigation background', category: 'layout', isGradient: true, usage: ['Header background'], gradientStops: [
                        { color: 'hsl(238 100% 67%)', position: 0 },
                        { color: 'hsl(238 100% 62%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-bg', value: '220 14% 97%', description: 'Sidebar background', category: 'layout', isGradient: true, usage: ['Sidebar background'], gradientStops: [
                        { color: 'hsl(220 14% 97%)', position: 0 },
                        { color: 'hsl(220 14% 95%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-active', value: '238 100% 67%', description: 'Active sidebar item', category: 'layout', isGradient: true, usage: ['Active menu items'], gradientStops: [
                        { color: 'hsl(238 100% 67%)', position: 0 },
                        { color: 'hsl(238 100% 62%)', position: 100 }
                    ]
                },
                { name: 'main-bg', value: '210 20% 98%', description: 'Main content background', category: 'layout', isGradient: false, usage: ['Main content area'] },
                { name: 'panel-bg', value: '0 0% 100%', description: 'Panel background', category: 'layout', isGradient: false, usage: ['Panel backgrounds'] },
                { name: 'card-bg', value: '0 0% 100%', description: 'Card background', category: 'layout', isGradient: false, usage: ['Card backgrounds'] },

                // Component colors
                {
                    name: 'button-bg-primary', value: '238 100% 67%', description: 'Primary button background', category: 'components', isGradient: true, usage: ['Primary buttons'], gradientStops: [
                        { color: 'hsl(238 100% 67%)', position: 0 },
                        { color: 'hsl(238 100% 62%)', position: 100 }
                    ]
                },
                {
                    name: 'button-bg-secondary', value: '215 16% 47%', description: 'Secondary button background', category: 'components', isGradient: true, usage: ['Secondary buttons'], gradientStops: [
                        { color: 'hsl(215 16% 47%)', position: 0 },
                        { color: 'hsl(215 16% 42%)', position: 100 }
                    ]
                },
                { name: 'button-bg-ghost', value: 'transparent', description: 'Ghost button background', category: 'components', isGradient: false, usage: ['Ghost buttons'] },
                {
                    name: 'badge-bg-neutral', value: '220 20% 95%', description: 'Neutral badge background', category: 'components', isGradient: true, usage: ['Neutral badges'], gradientStops: [
                        { color: 'hsl(220 20% 95%)', position: 0 },
                        { color: 'hsl(220 15% 90%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-info', value: '200 100% 95%', description: 'Info badge background', category: 'components', isGradient: true, usage: ['Info badges'], gradientStops: [
                        { color: 'hsl(200 100% 95%)', position: 0 },
                        { color: 'hsl(200 80% 88%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-success', value: '140 60% 92%', description: 'Success badge background', category: 'components', isGradient: true, usage: ['Success badges'], gradientStops: [
                        { color: 'hsl(140 60% 92%)', position: 0 },
                        { color: 'hsl(140 50% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-warning', value: '45 90% 92%', description: 'Warning badge background', category: 'components', isGradient: true, usage: ['Warning badges'], gradientStops: [
                        { color: 'hsl(45 90% 92%)', position: 0 },
                        { color: 'hsl(45 80% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-danger', value: '0 70% 92%', description: 'Danger badge background', category: 'components', isGradient: true, usage: ['Danger badges'], gradientStops: [
                        { color: 'hsl(0 70% 92%)', position: 0 },
                        { color: 'hsl(0 60% 85%)', position: 100 }
                    ]
                },
            ]
        },
        {
            id: 'rose',
            name: 'Rose Gold',
            description: 'Warm rose colors',
            colors: [
                // Core colors
                { name: 'background', value: '210 20% 98%', description: 'Main background color', category: 'core', isGradient: false, usage: ['Main page background', 'Card backgrounds'] },
                { name: 'foreground', value: '224 71% 4%', description: 'Main text color', category: 'core', isGradient: false, usage: ['Primary text', 'Headings'] },
                { name: 'primary', value: '330 81% 60%', description: 'Primary brand color', category: 'core', isGradient: false, usage: ['Primary buttons', 'Links', 'Accents'] },
                { name: 'secondary', value: '215 16% 47%', description: 'Secondary brand color', category: 'core', isGradient: false, usage: ['Secondary buttons', 'Secondary text'] },
                { name: 'accent', value: '330 81% 60%', description: 'Accent color for highlights', category: 'core', isGradient: false, usage: ['Hover states', 'Focus rings'] },
                { name: 'muted', value: '220 14% 96%', description: 'Muted background color', category: 'core', isGradient: false, usage: ['Muted backgrounds', 'Disabled states'] },
                { name: 'border', value: '214 32% 91%', description: 'Border color', category: 'core', isGradient: false, usage: ['Borders', 'Dividers'] },
                { name: 'ring', value: '330 81% 60%', description: 'Focus ring color', category: 'core', isGradient: false, usage: ['Focus indicators'] },

                // Layout colors
                {
                    name: 'topbar-bg', value: '25 95% 53%', description: 'Top navigation background', category: 'layout', isGradient: true, usage: ['Header background'], gradientStops: [
                        { color: 'hsl(25 95% 53%)', position: 0 },
                        { color: 'hsl(25 95% 48%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-bg', value: '220 14% 97%', description: 'Sidebar background', category: 'layout', isGradient: true, usage: ['Sidebar background'], gradientStops: [
                        { color: 'hsl(220 14% 97%)', position: 0 },
                        { color: 'hsl(220 14% 95%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-active', value: '25 95% 53%', description: 'Active sidebar item', category: 'layout', isGradient: true, usage: ['Active menu items'], gradientStops: [
                        { color: 'hsl(25 95% 53%)', position: 0 },
                        { color: 'hsl(25 95% 48%)', position: 100 }
                    ]
                },
                { name: 'main-bg', value: '210 20% 98%', description: 'Main content background', category: 'layout', isGradient: false, usage: ['Main content area'] },
                { name: 'panel-bg', value: '0 0% 100%', description: 'Panel background', category: 'layout', isGradient: false, usage: ['Panel backgrounds'] },
                { name: 'card-bg', value: '0 0% 100%', description: 'Card background', category: 'layout', isGradient: false, usage: ['Card backgrounds'] },

                // Component colors
                {
                    name: 'button-bg-primary', value: '25 95% 53%', description: 'Primary button background', category: 'components', isGradient: true, usage: ['Primary buttons'], gradientStops: [
                        { color: 'hsl(25 95% 53%)', position: 0 },
                        { color: 'hsl(25 95% 48%)', position: 100 }
                    ]
                },
                {
                    name: 'button-bg-secondary', value: '215 16% 47%', description: 'Secondary button background', category: 'components', isGradient: true, usage: ['Secondary buttons'], gradientStops: [
                        { color: 'hsl(215 16% 47%)', position: 0 },
                        { color: 'hsl(215 16% 42%)', position: 100 }
                    ]
                },
                { name: 'button-bg-ghost', value: 'transparent', description: 'Ghost button background', category: 'components', isGradient: false, usage: ['Ghost buttons'] },
                {
                    name: 'badge-bg-neutral', value: '220 20% 95%', description: 'Neutral badge background', category: 'components', isGradient: true, usage: ['Neutral badges'], gradientStops: [
                        { color: 'hsl(220 20% 95%)', position: 0 },
                        { color: 'hsl(220 15% 90%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-info', value: '200 100% 95%', description: 'Info badge background', category: 'components', isGradient: true, usage: ['Info badges'], gradientStops: [
                        { color: 'hsl(200 100% 95%)', position: 0 },
                        { color: 'hsl(200 80% 88%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-success', value: '140 60% 92%', description: 'Success badge background', category: 'components', isGradient: true, usage: ['Success badges'], gradientStops: [
                        { color: 'hsl(140 60% 92%)', position: 0 },
                        { color: 'hsl(140 50% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-warning', value: '45 90% 92%', description: 'Warning badge background', category: 'components', isGradient: true, usage: ['Warning badges'], gradientStops: [
                        { color: 'hsl(45 90% 92%)', position: 0 },
                        { color: 'hsl(45 80% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-danger', value: '0 70% 92%', description: 'Danger badge background', category: 'components', isGradient: true, usage: ['Danger badges'], gradientStops: [
                        { color: 'hsl(0 70% 92%)', position: 0 },
                        { color: 'hsl(0 60% 85%)', position: 100 }
                    ]
                },
            ]
        },
        {
            id: 'ocean',
            name: 'Ocean Blue',
            description: 'Deep blue gradient',
            colors: [
                // Core colors
                { name: 'background', value: '210 20% 98%', description: 'Main background color', category: 'core', isGradient: false, usage: ['Main page background', 'Card backgrounds'] },
                { name: 'foreground', value: '224 71% 4%', description: 'Main text color', category: 'core', isGradient: false, usage: ['Primary text', 'Headings'] },
                { name: 'primary', value: '200 100% 50%', description: 'Primary brand color', category: 'core', isGradient: false, usage: ['Primary buttons', 'Links', 'Accents'] },
                { name: 'secondary', value: '215 16% 47%', description: 'Secondary brand color', category: 'core', isGradient: false, usage: ['Secondary buttons', 'Secondary text'] },
                { name: 'accent', value: '200 100% 50%', description: 'Accent color for highlights', category: 'core', isGradient: false, usage: ['Hover states', 'Focus rings'] },
                { name: 'muted', value: '220 14% 96%', description: 'Muted background color', category: 'core', isGradient: false, usage: ['Muted backgrounds', 'Disabled states'] },
                { name: 'border', value: '214 32% 91%', description: 'Border color', category: 'core', isGradient: false, usage: ['Borders', 'Dividers'] },
                { name: 'ring', value: '200 100% 50%', description: 'Focus ring color', category: 'core', isGradient: false, usage: ['Focus indicators'] },

                // Layout colors
                {
                    name: 'topbar-bg', value: '200 100% 50%', description: 'Top navigation background', category: 'layout', isGradient: true, usage: ['Header background'], gradientStops: [
                        { color: 'hsl(200 100% 50%)', position: 0 },
                        { color: 'hsl(200 100% 45%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-bg', value: '220 14% 97%', description: 'Sidebar background', category: 'layout', isGradient: true, usage: ['Sidebar background'], gradientStops: [
                        { color: 'hsl(220 14% 97%)', position: 0 },
                        { color: 'hsl(220 14% 95%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-active', value: '200 100% 50%', description: 'Active sidebar item', category: 'layout', isGradient: true, usage: ['Active menu items'], gradientStops: [
                        { color: 'hsl(200 100% 50%)', position: 0 },
                        { color: 'hsl(200 100% 45%)', position: 100 }
                    ]
                },
                { name: 'main-bg', value: '210 20% 98%', description: 'Main content background', category: 'layout', isGradient: false, usage: ['Main content area'] },
                { name: 'panel-bg', value: '0 0% 100%', description: 'Panel background', category: 'layout', isGradient: false, usage: ['Panel backgrounds'] },
                { name: 'card-bg', value: '0 0% 100%', description: 'Card background', category: 'layout', isGradient: false, usage: ['Card backgrounds'] },

                // Component colors
                {
                    name: 'button-bg-primary', value: '200 100% 50%', description: 'Primary button background', category: 'components', isGradient: true, usage: ['Primary buttons'], gradientStops: [
                        { color: 'hsl(200 100% 50%)', position: 0 },
                        { color: 'hsl(200 100% 45%)', position: 100 }
                    ]
                },
                {
                    name: 'button-bg-secondary', value: '215 16% 47%', description: 'Secondary button background', category: 'components', isGradient: true, usage: ['Secondary buttons'], gradientStops: [
                        { color: 'hsl(215 16% 47%)', position: 0 },
                        { color: 'hsl(215 16% 42%)', position: 100 }
                    ]
                },
                { name: 'button-bg-ghost', value: 'transparent', description: 'Ghost button background', category: 'components', isGradient: false, usage: ['Ghost buttons'] },
                {
                    name: 'badge-bg-neutral', value: '220 20% 95%', description: 'Neutral badge background', category: 'components', isGradient: true, usage: ['Neutral badges'], gradientStops: [
                        { color: 'hsl(220 20% 95%)', position: 0 },
                        { color: 'hsl(220 15% 90%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-info', value: '200 100% 95%', description: 'Info badge background', category: 'components', isGradient: true, usage: ['Info badges'], gradientStops: [
                        { color: 'hsl(200 100% 95%)', position: 0 },
                        { color: 'hsl(200 80% 88%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-success', value: '140 60% 92%', description: 'Success badge background', category: 'components', isGradient: true, usage: ['Success badges'], gradientStops: [
                        { color: 'hsl(140 60% 92%)', position: 0 },
                        { color: 'hsl(140 50% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-warning', value: '45 90% 92%', description: 'Warning badge background', category: 'components', isGradient: true, usage: ['Warning badges'], gradientStops: [
                        { color: 'hsl(45 90% 92%)', position: 0 },
                        { color: 'hsl(45 80% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-danger', value: '0 70% 92%', description: 'Danger badge background', category: 'components', isGradient: true, usage: ['Danger badges'], gradientStops: [
                        { color: 'hsl(0 70% 92%)', position: 0 },
                        { color: 'hsl(0 60% 85%)', position: 100 }
                    ]
                },
            ]
        },
        {
            id: 'amber',
            name: 'Amber Professional',
            description: 'Warm amber tones',
            colors: [
                // Core colors
                { name: 'background', value: '224 47% 5%', description: 'Main background color', category: 'core', isGradient: false, usage: ['Main page background', 'Card backgrounds'] },
                { name: 'foreground', value: '210 20% 98%', description: 'Main text color', category: 'core', isGradient: false, usage: ['Primary text', 'Headings'] },
                { name: 'primary', value: '45 93% 47%', description: 'Primary brand color', category: 'core', isGradient: false, usage: ['Primary buttons', 'Links', 'Accents'] },
                { name: 'secondary', value: '220 9% 32%', description: 'Secondary brand color', category: 'core', isGradient: false, usage: ['Secondary buttons', 'Secondary text'] },
                { name: 'accent', value: '45 93% 47%', description: 'Accent color for highlights', category: 'core', isGradient: false, usage: ['Hover states', 'Focus rings'] },
                { name: 'muted', value: '224 14% 12%', description: 'Muted background color', category: 'core', isGradient: false, usage: ['Muted backgrounds', 'Disabled states'] },
                { name: 'border', value: '220 14% 22%', description: 'Border color', category: 'core', isGradient: false, usage: ['Borders', 'Dividers'] },
                { name: 'ring', value: '45 93% 47%', description: 'Focus ring color', category: 'core', isGradient: false, usage: ['Focus indicators'] },

                // Layout colors
                {
                    name: 'topbar-bg', value: '224 47% 7%', description: 'Top navigation background', category: 'layout', isGradient: true, usage: ['Header background'], gradientStops: [
                        { color: 'hsl(224 47% 7%)', position: 0 },
                        { color: 'hsl(224 47% 5%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-bg', value: '224 47% 6%', description: 'Sidebar background', category: 'layout', isGradient: true, usage: ['Sidebar background'], gradientStops: [
                        { color: 'hsl(224 47% 6%)', position: 0 },
                        { color: 'hsl(224 47% 4%)', position: 100 }
                    ]
                },
                {
                    name: 'sidebar-active', value: '0 0% 100%', description: 'Active sidebar item', category: 'layout', isGradient: true, usage: ['Active menu items'], gradientStops: [
                        { color: 'hsl(0 0% 100%)', position: 0 },
                        { color: 'hsl(0 0% 90%)', position: 100 }
                    ]
                },
                { name: 'main-bg', value: '224 47% 5%', description: 'Main content background', category: 'layout', isGradient: false, usage: ['Main content area'] },
                { name: 'panel-bg', value: '228 15% 10%', description: 'Panel background', category: 'layout', isGradient: false, usage: ['Panel backgrounds'] },
                { name: 'card-bg', value: '228 15% 10%', description: 'Card background', category: 'layout', isGradient: false, usage: ['Card backgrounds'] },

                // Component colors
                {
                    name: 'button-bg-primary', value: '0 0% 100%', description: 'Primary button background', category: 'components', isGradient: true, usage: ['Primary buttons'], gradientStops: [
                        { color: 'hsl(0 0% 100%)', position: 0 },
                        { color: 'hsl(0 0% 90%)', position: 100 }
                    ]
                },
                {
                    name: 'button-bg-secondary', value: '220 9% 32%', description: 'Secondary button background', category: 'components', isGradient: true, usage: ['Secondary buttons'], gradientStops: [
                        { color: 'hsl(220 9% 32%)', position: 0 },
                        { color: 'hsl(220 9% 28%)', position: 100 }
                    ]
                },
                { name: 'button-bg-ghost', value: 'transparent', description: 'Ghost button background', category: 'components', isGradient: false, usage: ['Ghost buttons'] },
                {
                    name: 'badge-bg-neutral', value: '224 14% 12%', description: 'Neutral badge background', category: 'components', isGradient: true, usage: ['Neutral badges'], gradientStops: [
                        { color: 'hsl(224 14% 12%)', position: 0 },
                        { color: 'hsl(224 14% 8%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-info', value: '200 100% 95%', description: 'Info badge background', category: 'components', isGradient: true, usage: ['Info badges'], gradientStops: [
                        { color: 'hsl(200 100% 95%)', position: 0 },
                        { color: 'hsl(200 80% 88%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-success', value: '140 60% 92%', description: 'Success badge background', category: 'components', isGradient: true, usage: ['Success badges'], gradientStops: [
                        { color: 'hsl(140 60% 92%)', position: 0 },
                        { color: 'hsl(140 50% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-warning', value: '45 90% 92%', description: 'Warning badge background', category: 'components', isGradient: true, usage: ['Warning badges'], gradientStops: [
                        { color: 'hsl(45 90% 92%)', position: 0 },
                        { color: 'hsl(45 80% 85%)', position: 100 }
                    ]
                },
                {
                    name: 'badge-bg-danger', value: '0 70% 92%', description: 'Danger badge background', category: 'components', isGradient: true, usage: ['Danger badges'], gradientStops: [
                        { color: 'hsl(0 70% 92%)', position: 0 },
                        { color: 'hsl(0 60% 85%)', position: 100 }
                    ]
                },
            ]
        }
    ]);

    const currentPalette = palettes.find(p => p.id === selectedPalette) || palettes[0];

    const copyToClipboard = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedValue(value);
            setTimeout(() => setCopiedValue(null), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const hslToHex = (hsl: string) => {
        const [h, s, l] = hsl.split(' ').map(v => parseFloat(v.replace('%', '')));
        const hNorm = h / 360;
        const sNorm = s / 100;
        const lNorm = l / 100;

        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        let r, g, b;
        if (sNorm === 0) {
            r = g = b = lNorm;
        } else {
            const q = lNorm < 0.5 ? lNorm * (1 + sNorm) : lNorm + sNorm - lNorm * sNorm;
            const p = 2 * lNorm - q;
            r = hue2rgb(p, q, hNorm + 1 / 3);
            g = hue2rgb(p, q, hNorm);
            b = hue2rgb(p, q, hNorm - 1 / 3);
        }

        const toHex = (c: number) => {
            const hex = Math.round(c * 255).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };

        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const hexToHsl = (hex: string) => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0, s = 0, l = (max + min) / 2;

        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
            }
            h /= 6;
        }

        return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    const openColorEditor = (token: ColorToken) => {
        setEditingToken(token);
        setIsGradient(token.isGradient);
        if (token.gradientStops) {
            setGradientStops(token.gradientStops);
        } else {
            const hex = hslToHex(token.value);
            setGradientStops([
                { color: hex, position: 0 },
                { color: hex, position: 100 }
            ]);
        }
    };

    const updateToken = (tokenName: string, newValue: string, isGradient: boolean, gradientStops?: { color: string; position: number }[]) => {
        setPalettes(prev => prev.map(palette =>
            palette.id === selectedPalette
                ? {
                    ...palette,
                    colors: palette.colors.map(color =>
                        color.name === tokenName
                            ? { ...color, value: newValue, isGradient, gradientStops }
                            : color
                    )
                }
                : palette
        ));
        setHasUnsavedChanges(true);
    };

    const saveChanges = () => {
        // Here you would save to tokens.json
        console.log('Saving palette changes:', currentPalette);
        setHasUnsavedChanges(false);
        // TODO: Implement actual save to tokens.json
    };

    const resetChanges = () => {
        // Reload from original tokens.json
        window.location.reload();
    };

    const groupedColors = currentPalette.colors.reduce((acc, color) => {
        if (!acc[color.category]) {
            acc[color.category] = [];
        }
        acc[color.category].push(color);
        return acc;
    }, {} as Record<string, ColorToken[]>);

    return (
        <div className="min-h-screen bg-[hsl(var(--main-bg))] text-[hsl(var(--main-fg))] p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-[hsl(var(--main-fg))]">Palette Editor</h1>
                        <p className="text-[hsl(var(--muted-foreground))] mt-2">
                            Developer tool for editing design tokens and color palettes
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <DensityToggle />
                        <ThemeSwitcher />
                    </div>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-[hsl(var(--main-fg))]">Palette:</label>
                        <select
                            value={selectedPalette}
                            onChange={(e) => setSelectedPalette(e.target.value)}
                            className="px-3 py-2 border border-[hsl(var(--border))] rounded-md bg-[hsl(var(--background))] text-[hsl(var(--foreground))]"
                        >
                            {palettes.map((palette) => (
                                <option key={palette.id} value={palette.id}>
                                    {palette.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {hasUnsavedChanges && (
                        <div className="flex items-center gap-2">
                            <ThemeButton variant="primary" size="sm" onClick={saveChanges}>
                                <Save className="h-4 w-4 mr-2" />
                                Save Changes
                            </ThemeButton>
                            <ThemeButton variant="secondary" size="sm" onClick={resetChanges}>
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Reset
                            </ThemeButton>
                        </div>
                    )}
                </div>

                {/* Current Palette Info */}
                <ThemeCard className="mb-8">
                    <ThemeCardHeader>
                        <ThemeCardTitle className="flex items-center gap-2">
                            <Palette className="h-5 w-5" />
                            {currentPalette.name}
                        </ThemeCardTitle>
                        <p className="text-[hsl(var(--muted-foreground))]">{currentPalette.description}</p>
                    </ThemeCardHeader>
                </ThemeCard>
            </div>

            {/* Color Variables Grid */}
            <div className="space-y-8">
                {Object.entries(groupedColors).map(([category, colors]) => (
                    <div key={category}>
                        <h2 className="text-xl font-semibold text-[hsl(var(--main-fg))] mb-4 capitalize">
                            {category} Colors
                        </h2>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {colors.map((color) => (
                                <ThemeCard key={color.name} className="relative">
                                    <ThemeCardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            {/* Color Preview */}
                                            <div
                                                className="w-12 h-12 rounded-lg border border-[hsl(var(--border))] flex-shrink-0 cursor-pointer"
                                                style={{
                                                    background: color.isGradient && color.gradientStops
                                                        ? `linear-gradient(135deg, ${color.gradientStops.map(stop => stop.color).join(', ')})`
                                                        : `hsl(${color.value})`
                                                }}
                                                onClick={() => openColorEditor(color)}
                                                title="Click to edit"
                                            />

                                            {/* Color Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <code className="text-sm font-mono text-[hsl(var(--primary))] bg-[hsl(var(--muted))] px-2 py-1 rounded">
                                                        {color.name}
                                                    </code>
                                                    <button
                                                        onClick={() => copyToClipboard(color.value)}
                                                        className="p-1 hover:bg-[hsl(var(--muted))] rounded transition-colors"
                                                        title="Copy HSL value"
                                                    >
                                                        {copiedValue === color.value ? (
                                                            <Check className="h-3 w-3 text-green-600" />
                                                        ) : (
                                                            <Copy className="h-3 w-3" />
                                                        )}
                                                    </button>
                                                </div>
                                                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">
                                                    {color.description}
                                                </p>
                                                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">
                                                    <strong>Usage:</strong> {color.usage.join(', ')}
                                                </div>
                                                <code className="text-xs text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] px-2 py-1 rounded block">
                                                    {color.isGradient ? 'Gradient' : `hsl(${color.value})`}
                                                </code>
                                            </div>
                                        </div>
                                    </ThemeCardContent>
                                </ThemeCard>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Color Editor Modal */}
            {editingToken && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--border))] rounded-lg p-6 w-full max-w-2xl mx-4">
                        <h3 className="text-lg font-semibold mb-4">Edit {editingToken.name}</h3>

                        <div className="space-y-4">
                            {/* Gradient Toggle */}
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="isGradient"
                                    checked={isGradient}
                                    onChange={(e) => setIsGradient(e.target.checked)}
                                    className="rounded"
                                />
                                <label htmlFor="isGradient" className="text-sm">Use gradient</label>
                            </div>

                            {isGradient ? (
                                /* Gradient Editor */
                                <div className="space-y-4">
                                    <div className="text-sm font-medium">Gradient Stops:</div>
                                    {gradientStops.map((stop, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={stop.color}
                                                onChange={(e) => {
                                                    const newStops = [...gradientStops];
                                                    newStops[index].color = e.target.value;
                                                    setGradientStops(newStops);
                                                }}
                                                className="w-12 h-8 rounded border"
                                            />
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={stop.position}
                                                onChange={(e) => {
                                                    const newStops = [...gradientStops];
                                                    newStops[index].position = parseInt(e.target.value);
                                                    setGradientStops(newStops);
                                                }}
                                                className="flex-1"
                                            />
                                            <span className="text-sm w-12">{stop.position}%</span>
                                            {gradientStops.length > 2 && (
                                                <button
                                                    onClick={() => {
                                                        const newStops = gradientStops.filter((_, i) => i !== index);
                                                        setGradientStops(newStops);
                                                    }}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        onClick={() => {
                                            setGradientStops([...gradientStops, { color: '#000000', position: 50 }]);
                                        }}
                                        className="flex items-center gap-2 text-sm text-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]/80"
                                    >
                                        <Plus className="h-4 w-4" />
                                        Add Stop
                                    </button>
                                </div>
                            ) : (
                                /* Solid Color Editor */
                                <div className="space-y-4">
                                    <label className="text-sm font-medium">Color Picker:</label>
                                    <div className="flex items-center gap-4">
                                        <ColorPicker
                                            color={`hsl(${editingToken.value})`}
                                            onChange={(newColor) => {
                                                // Extract HSL values from the color string
                                                const match = newColor.match(/hsl\((\d+)\s+(\d+)%\s+(\d+)%\)/);
                                                if (match) {
                                                    const hslValue = `${match[1]} ${match[2]}% ${match[3]}%`;
                                                    updateToken(editingToken.name, hslValue, false);
                                                }
                                            }}
                                        />
                                        <div className="flex-1">
                                            <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">HSL Value:</label>
                                            <input
                                                type="text"
                                                value={editingToken.value}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    updateToken(editingToken.name, newValue, false);
                                                }}
                                                className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-md bg-[hsl(var(--background))] text-[hsl(var(--foreground))] text-sm"
                                                placeholder="e.g., 221 83% 53%"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Preview */}
                            <div className="space-y-2">
                                <div className="text-sm font-medium">Preview:</div>
                                <div
                                    className="w-full h-16 rounded-lg border border-[hsl(var(--border))]"
                                    style={{
                                        background: isGradient
                                            ? `linear-gradient(135deg, ${gradientStops.map(stop => stop.color).join(', ')})`
                                            : `hsl(${editingToken.value})`
                                    }}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <ThemeButton variant="secondary" onClick={() => setEditingToken(null)}>
                                Cancel
                            </ThemeButton>
                            <ThemeButton
                                variant="primary"
                                onClick={() => {
                                    if (isGradient) {
                                        updateToken(editingToken.name, '', true, gradientStops);
                                    } else {
                                        updateToken(editingToken.name, editingToken.value, false);
                                    }
                                    setEditingToken(null);
                                }}
                            >
                                Save
                            </ThemeButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
